import './input.scss';

import {
  type InputHTMLAttributes,
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';

import AlertCircleIcon from '@/media/icons/alert-circle.svg?react';

export type TInputFieldSize = 'default' | 'compact';

export type IInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size'
> & {
  label?: string;
  error?: string;
  endContent?: React.ReactNode;
  startContent?: React.ReactNode;
  isRequired?: boolean;
  fieldSize?: TInputFieldSize;
  classes?: {
    root?: string;
    label?: string;
    error?: string;
    control?: string;
  };
  fileName?: string;
  isFrozen?: boolean;
  errorWithoutMessage?: boolean;
};

const Input = forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      label,
      classes,
      error,
      isRequired,
      endContent,
      startContent,
      fieldSize = 'default',
      fileName,
      isFrozen,
      errorWithoutMessage,
      id: idProp,
      ...rest
    },
    forwardedRef
  ) => {
    const { t } = useTranslation();
    const generatedId = useId();
    const fieldId = idProp ?? generatedId;
    const endContainerRef = useRef<HTMLDivElement>(null);
    const ref = useRef<HTMLInputElement>(null);
    useImperativeHandle(forwardedRef, () => ref.current as HTMLInputElement);

    useEffect(() => {
      // Увеличиваем размер шрифта для safari, чтобы нормально отображались поля
      if (
        /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
        ref.current &&
        fieldSize === 'default'
      ) {
        ref.current.style.fontSize = '16px';
      }
    }, [fieldSize]);

    const isInputFile = rest.type === 'file';

    const rootClass = [
      'input',
      fieldSize === 'compact' ? 'input--compact' : '',
      classes?.root ? classes.root : ''
    ].join(' ');

    const wrapperClass = [
      'input__wrapper',
      isInputFile ? 'input__wrapper--with-file' : '',
      isInputFile && error !== undefined ? 'input__field--error' : '',
      !label ? 'input__wrapper--without-label' : ''
    ].join(' ');

    const labelClass = [
      classes?.label ? classes.label : '',
      rest.value || fileName ? 'input__label--focused' : ''
    ].join(' ');

    const errorClass = classes?.error ? classes.error : '';

    const fieldClass = [
      rest.className ? rest.className : '',
      error !== undefined ? 'input__field--error' : '',
      !label ? 'input__field--without-label' : '',
      isInputFile ? 'input__field--file' : '',
      isFrozen ? 'input__field--frozen' : ''
    ].join(' ');

    const inputPadding = endContainerRef.current
      ? `${endContainerRef.current.getBoundingClientRect().width || 44}px`
      : undefined;

    return (
      <div className={rootClass}>
        <label className={wrapperClass} htmlFor={fieldId}>
          {startContent ? (
            <div className="input__start">{startContent}</div>
          ) : null}

          <input
            readOnly={isFrozen}
            inputMode={isFrozen ? 'none' : undefined}
            {...rest}
            id={fieldId}
            ref={ref}
            type={isFrozen && isInputFile ? 'text' : rest.type}
            style={{ paddingRight: inputPadding, ...rest.style }}
            placeholder={!label || rest.placeholder ? rest.placeholder : ''}
            value={rest.value}
            className={`input__field ${fieldClass}`}
            onChange={isFrozen ? () => null : rest.onChange}
          />

          {isInputFile ? (
            <span
              className="input__file-name"
              style={{
                opacity: fileName ? '1' : '0',
                width: `calc(100% - ${inputPadding})`
              }}
            >
              {fileName ?? t('ui.input.noFile')}
            </span>
          ) : null}

          {label ? (
            <p
              style={{ width: endContent ? '75%' : undefined }}
              className={`input__label ${labelClass}`}
            >
              {label}
              {isRequired ? <span className="input__require">*</span> : null}
            </p>
          ) : null}

          {endContent ? (
            <div ref={endContainerRef} className="input__end-container">
              {endContent}
            </div>
          ) : null}
        </label>

        {error && !errorWithoutMessage ? (
          <p
            className={`input__error ${errorClass}`}
            onClick={() => ref.current?.focus()}
          >
            <AlertCircleIcon />
            {error}
          </p>
        ) : null}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
